<div class="container-fluid py-4">
  <div class="row">
    <div class="col-12">
      <!-- Header with Offline Indicator -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2 class="mb-0">
          <i class="fas fa-wifi me-2"></i>
          Offline Capability Demo
        </h2>
        <app-offline-indicator [compact]="false" [showNetworkInfo]="true"></app-offline-indicator>
      </div>

      <!-- Feature Detection -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="fas fa-cog me-2"></i>
                Browser Feature Support
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 col-lg-4 mb-2" *ngFor="let feature of getAvailableFeatures()">
                  <div class="d-flex align-items-center">
                    <i class="fas me-2" 
                       [class.fa-check-circle]="feature.available" 
                       [class.text-success]="feature.available"
                       [class.fa-times-circle]="!feature.available"
                       [class.text-danger]="!feature.available"></i>
                    <div>
                      <strong>{{ feature.name }}</strong>
                      <br>
                      <small class="text-muted">{{ feature.description }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- PWA Installation -->
      <div class="row mb-4" *ngIf="canInstall || !isInstalled">
        <div class="col-12">
          <div class="card border-primary">
            <div class="card-header bg-primary text-white">
              <h5 class="mb-0">
                <i class="fas fa-mobile-alt me-2"></i>
                Progressive Web App (PWA)
              </h5>
            </div>
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <p class="mb-2">
                    <strong>Install RARR as an app</strong> for offline access and better performance.
                  </p>
                  <small class="text-muted">
                    The app will work offline and provide native app-like experience.
                  </small>
                </div>
                <button 
                  *ngIf="canInstall" 
                  class="btn btn-primary"
                  (click)="installPWA()">
                  <i class="fas fa-download me-2"></i>
                  Install App
                </button>
                <span *ngIf="isInstalled" class="badge bg-success fs-6">
                  <i class="fas fa-check me-1"></i>
                  App Installed
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics Dashboard -->
      <div class="row mb-4">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card text-center">
            <div class="card-body">
              <i class="fas fa-database fa-2x text-info mb-2"></i>
              <h4 class="mb-1">{{ offlineStats.totalItems }}</h4>
              <p class="text-muted mb-0">Total Items</p>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card text-center">
            <div class="card-body">
              <i class="fas fa-check-circle fa-2x text-success mb-2"></i>
              <h4 class="mb-1">{{ offlineStats.syncedItems }}</h4>
              <p class="text-muted mb-0">Synced</p>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card text-center">
            <div class="card-body">
              <i class="fas fa-clock fa-2x text-warning mb-2"></i>
              <h4 class="mb-1">{{ offlineStats.pendingSync }}</h4>
              <p class="text-muted mb-0">Pending Sync</p>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card text-center">
            <div class="card-body">
              <i class="fas fa-hdd fa-2x text-primary mb-2"></i>
              <h4 class="mb-1">{{ getStorageUsagePercentage() }}%</h4>
              <p class="text-muted mb-0">Storage Used</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Demo Item -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="fas fa-plus me-2"></i>
                Add Demo Item (Works Offline)
              </h5>
            </div>
            <div class="card-body">
              <form (ngSubmit)="addDemoItem()" #demoForm="ngForm">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="itemTitle" class="form-label">Title *</label>
                    <input 
                      type="text" 
                      id="itemTitle"
                      class="form-control" 
                      [(ngModel)]="newItem.title"
                      name="title"
                      required
                      placeholder="Enter item title">
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="itemContent" class="form-label">Content</label>
                    <textarea 
                      id="itemContent"
                      class="form-control" 
                      [(ngModel)]="newItem.content"
                      name="content"
                      rows="3"
                      placeholder="Enter item content (optional)"></textarea>
                  </div>
                </div>
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  [disabled]="!newItem.title.trim()">
                  <i class="fas fa-plus me-2"></i>
                  Add Item
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Demo Items List -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <i class="fas fa-list me-2"></i>
                Demo Items ({{ demoItems.length }})
              </h5>
              <div class="btn-group btn-group-sm">
                <button 
                  class="btn btn-outline-primary"
                  (click)="manualSync()"
                  [disabled]="!networkStatus?.isOnline || syncInProgress"
                  title="Sync pending items">
                  <i class="fas fa-sync" [class.fa-spin]="syncInProgress"></i>
                  {{ syncInProgress ? 'Syncing...' : 'Sync Now' }}
                </button>
                <button 
                  class="btn btn-outline-secondary"
                  (click)="exportOfflineData()"
                  title="Export all offline data">
                  <i class="fas fa-download me-1"></i>
                  Export
                </button>
              </div>
            </div>
            <div class="card-body">
              <div *ngIf="demoItems.length === 0" class="text-center py-4 text-muted">
                <i class="fas fa-inbox fa-3x mb-3"></i>
                <p>No items yet. Add some items to test offline functionality!</p>
              </div>
              
              <div *ngIf="demoItems.length > 0" class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Content</th>
                      <th>Created</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let item of demoItems; trackBy: trackByItemId">
                      <td>
                        <strong>{{ item.title }}</strong>
                      </td>
                      <td>
                        <span class="text-truncate d-inline-block" style="max-width: 200px;">
                          {{ item.content }}
                        </span>
                      </td>
                      <td>
                        <span [title]="formatTimestamp(item.timestamp)">
                          {{ formatRelativeTime(item.timestamp) }}
                        </span>
                      </td>
                      <td>
                        <span class="badge" 
                              [class.bg-success]="item.synced"
                              [class.bg-warning]="!item.synced">
                          <i class="fas me-1" 
                             [class.fa-check]="item.synced"
                             [class.fa-clock]="!item.synced"></i>
                          {{ item.synced ? 'Synced' : 'Pending' }}
                        </span>
                      </td>
                      <td>
                        <button 
                          class="btn btn-sm btn-outline-danger"
                          (click)="deleteDemoItem(item)"
                          title="Delete item">
                          <i class="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Testing and Management Tools -->
      <div class="row mb-4">
        <div class="col-md-6 mb-3">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-tools me-2"></i>
                Testing Tools
              </h6>
            </div>
            <div class="card-body">
              <p class="small text-muted mb-3">
                Test offline functionality and network simulation.
              </p>
              <div class="d-grid gap-2">
                <button 
                  class="btn btn-outline-warning"
                  (click)="simulateOffline()">
                  <i class="fas fa-wifi-slash me-2"></i>
                  Simulate Offline
                </button>
                <button 
                  *ngIf="updateAvailable"
                  class="btn btn-outline-info"
                  (click)="activateUpdate()">
                  <i class="fas fa-sync-alt me-2"></i>
                  Activate Update
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-md-6 mb-3">
          <div class="card border-danger">
            <div class="card-header bg-danger text-white">
              <h6 class="mb-0">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Data Management
              </h6>
            </div>
            <div class="card-body">
              <p class="small text-muted mb-3">
                Clear offline data and cache (use with caution).
              </p>
              <div class="d-grid gap-2">
                <button 
                  class="btn btn-outline-danger"
                  (click)="clearOfflineData()">
                  <i class="fas fa-trash me-2"></i>
                  Clear Offline Data
                </button>
                <button 
                  class="btn btn-outline-danger"
                  (click)="clearCache()">
                  <i class="fas fa-broom me-2"></i>
                  Clear App Cache
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Last Sync Info -->
      <div class="row" *ngIf="offlineStats.lastSync">
        <div class="col-12">
          <div class="alert alert-info">
            <i class="fas fa-info-circle me-2"></i>
            <strong>Last Sync:</strong> {{ formatTimestamp(offlineStats.lastSync) }}
            ({{ formatRelativeTime(offlineStats.lastSync) }})
          </div>
        </div>
      </div>

      <!-- Instructions -->
      <div class="row">
        <div class="col-12">
          <div class="card bg-light">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-info-circle me-2"></i>
                How to Test Offline Functionality
              </h6>
            </div>
            <div class="card-body">
              <ol class="mb-0">
                <li>Add some demo items while online - they will sync automatically</li>
                <li>Go offline using browser dev tools (Network tab → Offline) or turn off your internet</li>
                <li>Add more items - they will be stored locally and marked as "Pending"</li>
                <li>Go back online - pending items will automatically sync</li>
                <li>Install as PWA for the full offline app experience</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>